/**
 * The cockpit's deterministic checks — ported 1:1 from the POC (poc/product-cockpit/js/util.js).
 *
 * Every function here is a MEASUREMENT, not an opinion: same input, same answer, no model
 * involved. That is what lets the UI print a ✓ / ≈ / ? next to a value and be believed.
 * Semantic synonymy is deliberately NOT guessed — deciding that two different words mean
 * the same thing is the semantic-gap research's job, not this file's.
 *
 * The port is checked against the real RunnerInn catalogue, not only against fixtures:
 * see docs/delivery/stories/5636/tech/design.md (PR2b, golden measures).
 */

import type { EnrichedAttribute, DictionaryEntry, FeedField, CockpitProduct } from '@/services/productCockpitService';

/**
 * Feed columns that carry no product semantics. They are excluded from provenance
 * matching so that an id, a URL or a date cannot "explain" a learned value.
 */
export const FEED_META_KEYS: ReadonlySet<string> = new Set([
  'product_id', 'item_group_id', 'feed_status', 'created_at',
  'last_seen_in_feed_at', 'image_url', 'product_url', 'currency', 'description',
]);

/**
 * Shared VALUE normalisation: case, separators and whitespace — NEVER substring.
 *
 * ⛔ A '-' THAT OPENS A NUMBER IS A SIGN, NOT A SEPARATOR. Collapsing every '-' to a space
 * made `down to -5 °C` and `down to 5 °C` the same string, and all three consumers then
 * agreed on the wrong answer at once: valueCheck called a REVERSED temperature a formatting
 * variant, feedOriginMatch claimed the merchant had provided the learned value, and
 * merchantLedger counted it an exact match. One erased character, three green results over a
 * real specification change.
 *
 * The sign is kept only where it functions as one — preceded by no alphanumeric and followed
 * by a digit — so `e-bike` is still `e bike` and the range `5-10 mm` is still `5 10 mm`.
 */
/**
 * Unicode has several characters that MEAN the ASCII hyphen-minus. The backend's value
 * normaliser folds them; this one did not, and the divergence was a live defect: merchant
 * '−5 °C' written with U+2212 left the sign orphaned as a word, so its 5 matched a learned
 * '5 °C' and a BELOW-freezing merchant value was reported as supporting an ABOVE-freezing
 * learned one. The reverse — the same value written with the two different glyphs — read as
 * no match at all.
 */
const DASHES = /[\u2212\u2012\u2013\u2014\u2015\u2010\u2011]/g;

export function normVal(v: unknown): string {
  const joined = Array.isArray(v) ? v.join(', ') : v;
  return String(joined ?? '').replace(DASHES, '-').trim().toLowerCase()
    .replace(/[_-]+/g, (m, off: number, str: string) => {
      const before = off === 0 ? '' : str[off - 1];
      const after = str[off + m.length] ?? '';
      if (m === '-' && /\d/.test(after) && !/[0-9a-z]/.test(before)) return m;
      return ' ';
    })
    .replace(/\s+/g, ' ');
}

/**
 * `spec:Weight` and `weight` are the same field name.
 *
 * Field names have their own normalisation on purpose: separator collapsing is exactly what
 * a NAME wants (`temp_min`, `temp-min`, `temp min` are one field), and it is exactly what a
 * VALUE must not have blanket-applied to it — see normVal. Sharing one function is what let
 * a rule written for names silently decide a numeric value.
 */
function normKey(k: unknown): string {
  const joined = Array.isArray(k) ? k.join(', ') : k;
  return String(joined ?? '').replace(DASHES, '-').replace(/^spec:/, '').trim().toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export type ValueCheckState = 'ok' | 'variant' | 'unvalidated';

export interface ValueCheck {
  state: ValueCheckState;
  /** how many known values the dictionary lists for this attribute */
  known: number;
  /** for `variant`: the known value it equals up to case/format */
  match?: string;
}

/**
 * Is this extracted value one of the category's known values?
 *
 * Three deterministic outcomes:
 *  - `ok`          — exact member of the dictionary's enum
 *  - `variant`     — equal up to case / separators / spacing: a spelling or format issue,
 *                    which synonym management turns green
 *  - `unvalidated` — absent: a wrong value, a true synonym, or a value the dictionary has
 *                    yet to learn. Which one it is, the semantic-gap research decides.
 *
 * Returns `null` when there is nothing to validate against (no enum, or an empty value):
 * that is "not checkable", which is NOT the same as "wrong".
 */
export function valueCheck(entry: DictionaryEntry | null | undefined, value: unknown): ValueCheck | null {
  const evs = entry?.enum_values;
  if (!Array.isArray(evs) || !evs.length) return null;
  const raw = (Array.isArray(value) ? value.join(', ') : String(value ?? '')).trim();
  if (!raw) return null;
  if (evs.some((v) => String(v).trim() === raw)) return { state: 'ok', known: evs.length };
  const m = evs.find((v) => normVal(v) === normVal(raw));
  if (m !== undefined) return { state: 'variant', match: String(m), known: evs.length };
  return { state: 'unvalidated', known: evs.length };
}

export type OriginKind = 'feed' | 'ai' | 'vendor' | 'inferred' | 'missing';

/** Bucket a recorded origin string into the five kinds the UI has chips for. */
export function originKind(origin: string | null | undefined): OriginKind {
  if (!origin) return 'missing';
  const o = origin.toLowerCase();
  if (o.includes('feed') || o === 'original') return 'feed';
  if (o.includes('vendor')) return 'vendor';
  if (o.includes('infer')) return 'inferred';
  if (o.includes('ai') || o.includes('image')) return 'ai';
  return 'missing';
}

/**
 * Deterministic feed-provenance recovery: a learned value with NO recorded origin that is
 * exactly equal to a merchant field did not need enrichment to exist, so the UI shows it as
 * feed-origin (computed, marked as such) rather than letting "unrecorded" swallow real feed
 * provenance.
 *
 * Equality is on the FULL normalised value, never a substring, so a name fragment cannot
 * false-positive. Boolean-ish values ("yes"/"no") carry no information of their own and may
 * only claim provenance from a field of the SAME name — otherwise every "yes" would match
 * every yes-field in the feed.
 *
 * @returns the merchant field key that explains the value, or null
 */
export function feedOriginMatch(
  product: Pick<CockpitProduct, 'tab1_original'> | null | undefined,
  attr: Pick<EnrichedAttribute, 'key' | 'value'>,
): string | null {
  const t1 = product?.tab1_original;
  // raw_fields are pre-curated server-side, so only the mapped columns need the meta filter
  const candidates: FeedField[] = (t1?.fields ?? [])
    .filter((f) => !FEED_META_KEYS.has(f.key))
    .concat(t1?.raw_fields ?? []);

  const av = normVal(attr.value);
  if (!av || av.length < 2) return null;
  const generic = av === 'yes' || av === 'no' || av === 'true' || av === 'false';
  const ak = normKey(attr.key);

  let hit: string | null = null;
  for (const f of candidates) {
    if (normVal(f.value) !== av) continue;
    if (normKey(f.key) === ak) return f.key;   // same-name field wins outright
    if (!generic && !hit) hit = f.key;
  }
  return hit;
}

export type LedgerRelation = 'exact' | 'normalized' | 'derived' | 'new';

export interface LedgerPair {
  attr: EnrichedAttribute;
  field: FeedField | null;
  rel: LedgerRelation;
}

export interface MerchantLedger {
  pairs: LedgerPair[];
  /** merchant fields no learned attribute paired with */
  leftovers: FeedField[];
  counts: Record<LedgerRelation, number>;
}

/**
 * Boolean-ish values carry no information of their own: every "yes" equals every other
 * "yes". `feedOriginMatch` has always refused to let them claim a differently-named field;
 * merchantLedger did not, so a learned `recycled: "yes"` paired as `exact` with a merchant
 * `spec:Waterproof: "yes"`, inflating the "= from feed" count AND consuming an unrelated
 * field so it could never pair with its own counterpart. Same rule, both places.
 */
function isGeneric(v: string): boolean {
  return v === 'yes' || v === 'no' || v === 'true' || v === 'false';
}

/**
 * Words that REVERSE a value. One side carrying one and the other not is a contradiction,
 * never a derivation — the same rule the backend's drift comparator applies, for the same
 * reason: a looser match must still refuse to match across a change of meaning.
 */
const CONTRADICTS: ReadonlySet<string> = new Set([
  'not', 'no', 'non', 'none', 'never', 'without', 'excluding', 'except', 'sans',
  'incompatible', 'unsupported', 'unavailable', 'absent', 'disabled', 'off',
  // a bound on one side only is the same contradiction: '5 mm' is not '>5 mm'
  // NB the unit joiners '/' and '·' are deliberately NOT here. A bare joiner anywhere in the
  // merchant value would block every derivation from it — '5 kg' would stop deriving from
  // '5 kg / m³ density, 5 kg mass', where the merchant states the mass outright. The joiner
  // is checked where it means something: immediately after the shared unit, in derivesFrom.
  '<', '>', '≤', '≥', '~', '≈', '±',
  // and the WORDS that mean the same as those symbols — '5 mm maximum' is '≤ 5 mm' spelled
  // out, and dropping it attributed a bounded merchant value to an unbounded learned one
  'to', 'up', 'from', 'least', 'most', 'max', 'maximum', 'min', 'minimum', 'upto',
  'under', 'over', 'above', 'below', 'between', 'approx', 'approximately', 'about',
  'circa', 'around', 'nearly', 'almost', 'roughly',
]);

/**
 * Words too common to carry a match on their own. This replaces a 2-character length floor
 * that was doing the job badly in both directions: it let 'of' through (2 chars) while
 * DELETING every single-letter unit — 'm', 'l', 'g', and the 'c'/'f' of a temperature — so
 * learned '5 m' matched merchant '5 kg' and '5 °C' matched '5 °F' on the strength of the
 * number alone.
 */
/**
 * One tokenisation, used by both the token bag and the measurement reader.
 *
 * ⛔ A DECIMAL IS ONE NUMBER. Reading only `\d+(\.\d+)?`, the fractional half of a
 * decimal-comma value became a measurement of its own: '1,5 kg' parsed as (1) and (5, [kg]),
 * so learned '5 kg' derived from merchant '1,5 kg'. '0,5 mm' and '.5 kg' did the same. The
 * numeric alternative now reads the whole literal, leading dot or comma included.
 */
const TOKEN_RE = /-?(?:\d+(?:[.,]\d+)?|[.,]\d+)|[\p{L}\p{No}]+(?:[/·^*][\p{L}\p{No}]+)*|\S/gu;

/**
 * One quantity: its number(s) and the unit they carry. `nums` holds MORE THAN ONE when the
 * value states a range — normVal turns the '-' of '5-10 kg' into a space, so the endpoints
 * arrive adjacent with nothing between them, and they belong to one quantity.
 */
interface Measurement { nums: string[]; unit: string[]; ambiguous: boolean }

/** does this token open a number? a leading '.' or ',' counts, a bare one does not */
const isNumeric = (t: string) => /^-?(?:\d|[.,]\d)/.test(t);

/**
 * A comma or dot followed by EXACTLY three digits is a thousands separator in one convention
 * and a decimal in another, and nothing in the value says which. A provenance claim may not
 * rest on a guess, so a measurement carrying one derives from nothing and nothing derives
 * from it — reported as `new`, which is the honest answer: we cannot tell.
 */
const isAmbiguousNumber = (t: string) => /^-?\d+[.,]\d{3}$/.test(t);

/**
 * Tokens that END a measurement: what follows belongs to the next one, not this unit.
 *
 * ⛔ '/' AND '·' ARE NOT HERE, AND PUTTING THEM HERE WAS THE DEFECT. They JOIN a unit —
 * 'm / s' is one quantity written with spaces — so treating them as separators cut the
 * denominator off, left '5 m / s' reading as the measurement (5, [m]), and learned '5 m'
 * then derived from merchant speed. '5 kg' derived from '5 kg / m³' the same way: distance
 * from speed, mass from density.
 */
const SEPARATORS: ReadonlySet<string> = new Set([',', ';', '|', '(', ')', '[', ']']);

/** the joiners that continue a unit rather than describing it — see derivesFrom */
const UNIT_JOINERS: ReadonlySet<string> = new Set(['/', '·', '^', '*']);

const NOISE_WORDS: ReadonlySet<string> = new Set([
  // NB 'to' is deliberately NOT here: it is a bound word ('5 to 10'), and a token cannot be
  // both noise to strip and meaning to compare — stripping it is what let a range pass as
  // its own endpoint.
  'a', 'an', 'the', 'of', 'and', 'or', 'in', 'on', 'for', 'with', 'at', 'by',
]);

/**
 * Tokens of a value, for the `derived` tier — split into the two things that must be
 * compared differently.
 *
 * ⛔ THE 2-CHARACTER FLOOR WAS SWALLOWING NUMBERS. It exists so that "a"/"of" cannot carry a
 * match, and applied to every token it deleted every single-digit quantity: '5 kg' reduced
 * to {kg} and matched a merchant '9 kg', which the UI then described as a merchant-derived
 * value and counted as normalised. Splitting on `[^a-z0-9]` also erased the sign, so
 * 'down to -5 °C' reduced to {down, to} and matched 'down to 5 °C' — the same reversed
 * temperature the value normaliser was fixed for two rounds ago, hiding one layer up.
 *
 * Numbers keep their sign and are never dropped for being short; the floor applies to WORDS
 * only, which is what it was written for.
 */
function valueTokens(v: unknown): { nums: string[]; words: Set<string> } {
  const nums: string[] = [];
  const words = new Set<string>();
  // ⛔ THE LAST ALTERNATIVE KEEPS EVERY GLYPH. Matching only known classes dropped units and
  // operators, so '5 m' matched '5 kg', '5 °C' matched '5 °F' and '5 mm' matched '>5 mm' —
  // the numbers agreed and everything that distinguished them had been thrown away. This is
  // the same fix the backend's tokeniser needed, in the second place the shape existed.
  // A COMPOUND UNIT IS ONE TOKEN. Keeping every glyph was not enough on its own: as three
  // loose tokens in an unordered set, 'm/s' and 's/m' are the same value, so learned '5 m/s'
  // derived from merchant '5 s/m' — speed from its own reciprocal. Letters, superscripts and
  // the joiners between them stay welded, exactly as the backend's tokeniser does it.
  for (const t of normVal(v).match(TOKEN_RE) ?? []) {
    if (isNumeric(t)) nums.push(String(Number.parseFloat(t.replace(',', '.'))));
    else if (!NOISE_WORDS.has(t)) words.add(t);
  }
  return { nums, words };
}

/**
 * A number and the unit that BELONGS to it.
 *
 * ⛔ COMPARING NUMBERS AND UNITS AS TWO SEPARATE BAGS LETS THEM COME FROM DIFFERENT
 * MEASUREMENTS. Learned '5 kg' matched merchant '5 m, 10 kg': the 5 was found (it belongs to
 * the metres) and 'kg' was found (it belongs to the 10), and nothing checked that they were
 * the same measurement. '5 °C' matched '5 °F, 10 °C' the same way. The ledger then showed ~
 * and counted an unsupported value as merchant-derived — the ledger's whole claim, inverted.
 *
 * The unit is every token after the number up to the next number or a separator, so
 * '5 m, 10 kg' is (5, [m]) and (10, [kg]) rather than one soup of {5, 10, m, kg}.
 */
function measurements(v: unknown): Measurement[] {
  const out: Measurement[] = [];
  let current: Measurement | null = null;
  for (const t of normVal(v).match(TOKEN_RE) ?? []) {
    if (isNumeric(t)) {
      const num = String(Number.parseFloat(t.replace(',', '.')));
      // ⛔ TWO ADJACENT NUMBERS ARE ONE QUANTITY. '5-10 kg' arrives as 5 and 10 with nothing
      // between them and the 'kg' on the second, so learned '10 kg' derived from a merchant
      // RANGE of 5 to 10 — an endpoint presented as the value. They join one measurement,
      // which then matches only a measurement stating the SAME range: '5-10 kg' still
      // derives from '5-10 kg net weight', and '10 kg' derives from neither.
      if (current && current.unit.length === 0) {
        current.nums.push(num);
        current.ambiguous = current.ambiguous || isAmbiguousNumber(t);
      } else {
        current = { nums: [num], unit: [], ambiguous: isAmbiguousNumber(t) };
        out.push(current);
      }
    } else if (SEPARATORS.has(t)) {
      current = null;
    } else if (current) {
      current.unit.push(t);
    }
  }
  return out;
}

/**
 * Is the learned value DERIVABLE from this merchant value?
 *
 * Loose by design — the merchant field may carry more than the learned attribute — but a
 * looser match is still a claim about provenance, so it may not cross:
 *  - a different QUANTITY: every number in the learned value must appear in the merchant's;
 *  - a reversed MEANING: a negation on one side and not the other is a contradiction.
 */
function derivesFrom(learned: unknown, merchant: unknown): boolean {
  const a = valueTokens(learned);
  const b = valueTokens(merchant);
  if (!a.nums.length && !a.words.size) return false;
  // Each learned MEASUREMENT must appear as one measurement in the merchant value: same
  // number, and its unit must open the merchant's unit for that same number — so '5 kg'
  // derives from '5 kg net weight' but not from '5 m, 10 kg'.
  const pool = measurements(merchant);
  for (const m of measurements(learned)) {
    // The learned unit must OPEN the merchant's — that is what keeps '5 kg' deriving from
    // '5 kg net weight'. But a unit JOINER immediately after the shared part means the
    // merchant's unit is a COMPOUND whose head is the learned one, which is a different
    // physical quantity: '5 m' does not derive from '5 m / s'.
    // an unreadable number cannot support a provenance claim in either direction
    if (m.ambiguous) return false;
    const at = pool.findIndex((x) => !x.ambiguous
      && x.nums.length === m.nums.length
      && x.nums.every((n, i) => n === m.nums[i])
      && m.unit.every((u, i) => x.unit[i] === u)
      && !UNIT_JOINERS.has(x.unit[m.unit.length]));
    if (at < 0) return false;
    pool.splice(at, 1);
  }
  for (const w of a.words) if (!b.words.has(w)) return false;
  // a qualifier on EITHER side that the other lacks reverses the meaning
  for (const w of a.words) if (CONTRADICTS.has(w) && !b.words.has(w)) return false;
  for (const w of b.words) if (CONTRADICTS.has(w) && !a.words.has(w)) return false;
  return true;
}

/**
 * Split-ledger pairing (Option A, settled 2026-09-01): pair each learned attribute with its
 * merchant counterpart so the Enrich step can show before and after side by side.
 *
 * Deterministic tiers, tried in this order:
 *  - `exact`      — value equality with a non-meta merchant field (same-name field preferred)
 *  - `normalized` — same field NAME, different value: the AI rewrote the merchant's value
 *  - `derived`    — every token of the learned value appears in ONE `spec:*` field's value
 *  - `new`        — no counterpart at all: net-new, and the actual value-add of enrichment
 */
export function merchantLedger(
  product: Pick<CockpitProduct, 'tab1_original' | 'tab2_ai_enrichment'> | null | undefined,
): MerchantLedger {
  const t1 = product?.tab1_original;
  const fields: FeedField[] = (t1?.fields ?? [])
    .filter((f) => f.status === 'used' && !FEED_META_KEYS.has(f.key))
    .concat(t1?.raw_fields ?? []);

  const attrs = product?.tab2_ai_enrichment?.attributes ?? [];
  const pairs: LedgerPair[] = [];
  const used = new Set<string>();

  for (const a of attrs) {
    const av = normVal(a.value);
    const ak = normKey(a.key);
    const long = !!av && av.length >= 2;

    const generic = isGeneric(av);
    let hit: FeedField | undefined = long
      ? fields.find((f) => normVal(f.value) === av && normKey(f.key) === ak)
        // a boolean-ish value may only match a field of the SAME name
        ?? (generic ? undefined : fields.find((f) => normVal(f.value) === av))
      : undefined;
    let rel: LedgerRelation | null = hit ? 'exact' : null;

    if (!hit) {
      hit = fields.find((f) => normKey(f.key) === ak);
      rel = hit ? 'normalized' : null;
    }
    if (!hit && !generic) {
      // same reason: "yes" is a subset of every spec containing the word
      hit = fields.find((f) => String(f.key).startsWith('spec:') && derivesFrom(a.value, f.value));
      rel = hit ? 'derived' : null;
    }
    if (hit) used.add(hit.key);
    pairs.push({ attr: a, field: hit ?? null, rel: rel ?? 'new' });
  }

  const counts: Record<LedgerRelation, number> = { exact: 0, normalized: 0, derived: 0, new: 0 };
  for (const p of pairs) counts[p.rel] += 1;
  return { pairs, leftovers: fields.filter((f) => !used.has(f.key)), counts };
}

export interface InterpretTally {
  ok: number;
  variant: number;
  unvalidated: number;
  /** attributes the category dictionary does not know at all */
  no_dictionary: number;
  /** dictionary entries with extraction hints but NO definition */
  incoherent: number;
}

/**
 * The Interpret step's headline, over one product's learned attributes.
 * Attributes with no checkable enum are counted in none of ok/variant/unvalidated —
 * "not checkable" is not a verdict.
 */
export function interpretTally(
  attrs: EnrichedAttribute[],
  dictionary: Record<string, DictionaryEntry> | null | undefined,
): InterpretTally {
  const t: InterpretTally = { ok: 0, variant: 0, unvalidated: 0, no_dictionary: 0, incoherent: 0 };
  for (const a of attrs) {
    const e = dictionary?.[a.key];
    if (!e) { t.no_dictionary += 1; continue; }
    if (e.extraction_hints && !e.definition) t.incoherent += 1;
    const vc = valueCheck(e, a.value);
    if (!vc) continue;
    if (vc.state === 'ok') t.ok += 1;
    else if (vc.state === 'variant') t.variant += 1;
    else t.unvalidated += 1;
  }
  return t;
}
